import { useEffect, useState } from 'react';

/** Anything the swipe deck can show. Only the id matters here. */
interface Identified {
  id: string;
}

/**
 * Holds one card still while the queue behind it reorders.
 *
 * Everybody swipes their own deck, and a "yes" from anybody else drops that
 * film into the front of yours. That has to be the *next* card, never the one
 * in your hand: swapping the card somebody is reading would cost them the
 * film they were looking at, and a swipe already in flight would land on a
 * title they never saw.
 *
 * So the card on screen is pinned by id and only let go when it leaves the
 * queue — which happens when *you* voted on it or set it aside, never because
 * somebody else did something. The next card is then whatever the queue now
 * puts first, fast lane included.
 */
export function usePinnedCard<T extends Identified>(queue: readonly T[]): T | null {
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const current = queue.find((item) => item.id === pinnedId) ?? queue[0] ?? null;

  useEffect(() => {
    if (current && current.id !== pinnedId) setPinnedId(current.id);
  }, [current, pinnedId]);

  return current;
}
